import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../ui";
import { cn } from "../../ui/utils/cn";
import { useAppSelector } from "../../store/hooks";
import ContributeChat from "./ContributeChat";
import MyContributions from "./MyContributions";

// /contribute — the public-facing "Contribute" entry. Anyone can open it; pitching
// requires login (we show a friendly prompt otherwise). Two tabs: the Idea Desk
// chatbot, and the member's own ideas with their status.

type Tab = "pitch" | "mine";

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 text-sm font-bold border-brutal border-ink rounded-sm transition-all",
        active ? "bg-mint shadow-brutal-sm" : "bg-paper hover:bg-accent"
      )}
    >
      {children}
    </button>
  );
}

export default function ContributePage() {
  const auth = useAppSelector((s) => s.auth);
  const [tab, setTab] = useState<Tab>("pitch");
  // remounting the chat resumes the member's latest draft (start reuses it).
  const [chatKey, setChatKey] = useState(0);

  const ready = auth.bootstrapStatus === "ready";
  const loggedIn = Boolean(auth.token);

  return (
    <div className="max-w-2xl mx-auto w-full px-4 py-6 md:py-10 md:pb-16">
      <header className="mb-4">
        <h1 className="text-2xl font-bold">Contribute</h1>
        <p className="text-sm text-ink/70 mt-1">
          Got an idea for n450s? Pitch it to the Idea Desk — talk it through, and if it gets built, it shows up as your
          contribution (with respect and a badge to show for it).
        </p>
      </header>

      {!ready ? (
        <div className="text-sm text-ink/50 p-4">loading…</div>
      ) : !loggedIn ? (
        <div className="border-brutal border-ink rounded-sm bg-paper shadow-brutal-md p-6 text-center space-y-3">
          <div className="text-base font-bold">Log in to pitch an idea 💡</div>
          <p className="text-sm text-ink/70">
            The Idea Desk is for members, so your contribution can be credited to you if it ships.
          </p>
          <Link to="/login">
            <Button variant="sky">log in</Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4">
            <TabButton active={tab === "pitch"} onClick={() => setTab("pitch")}>
              Pitch an idea
            </TabButton>
            <TabButton active={tab === "mine"} onClick={() => setTab("mine")}>
              My ideas
            </TabButton>
          </div>

          {tab === "pitch" ? (
            <ContributeChat key={chatKey} onGoToMyIdeas={() => setTab("mine")} />
          ) : (
            <MyContributions
              onResume={() => {
                setChatKey((k) => k + 1);
                setTab("pitch");
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
